import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Monitor, Bot, ChevronDown, ChevronRight, ArrowRight } from "lucide-react";

const softwareCategories = [
  {
    label: "Practice Management System",
    slug: "pms-scheduling",
    features: ["Patient Charts", "Perio Charting", "Ledgers & Billing", "Treatment Planning", "Scheduling", "Clinical Notes", "Cloud-Based — No Server", "Multi-User Access"],
  },
  {
    label: "Communication Suite",
    slug: "voip",
    features: ["VoIP Phone System", "Physical Desk Phones (Yealink)", "2-Way Texting", "Universal Inbox", "Team Chat & Channels", "Smart Voice Logs", "Intelligent Fax", "Next-Gen Digital Forms"],
  },
  {
    label: "Marketing Suite",
    slug: "sms-campaigns",
    features: ["SMS Campaigns", "Email Campaigns", "Automated Workflows", "Clinically-Aware Segmentation", "AI Handles Patient Replies"],
  },
  {
    label: "eClaims Portal",
    slug: "ariel",
    features: ["eClaims Built-In", "Clearinghouse Connectivity", "Single Login for Claims", "Integrated Claim Submission"],
  },
  {
    label: "Revenue Intelligence Suite",
    slug: "pms-revenue-analytics",
    features: ["Reporting & Analytics Dashboards", "KPI Tracking", "Collection Rate Insights", "Revenue Cycle Analytics", "Performance Metrics"],
  },
  {
    label: "Cloud Imaging Software",
    slug: "pms-imaging",
    features: ["SOTA Cloud Imaging", "DICOM Compatible", "Digital X-Ray Viewing", "Connects to Your Sensors", "Cloud Storage — No Local Server"],
  },
  {
    label: "Insurance Verification Software",
    slug: "amanda",
    features: ["Automated Verification", "Real-Time Eligibility Checks", "Benefits Breakdown Upload", "Dual-Source Verification"],
  },
  {
    label: "Integrated Payments Suite",
    slug: "text-to-pay",
    features: ["Text-to-Pay Payments", "Payment Card Readers", "Multi-Payment Method Support", "Automated Payment Reminders"],
  },
  {
    label: "Patient Portal",
    slug: "forms",
    features: ["Secure Patient Links", "Interactive Forms", "Treatment Plan Viewing", "Insurance Info Upload"],
  },
  {
    label: "ePrescribe (Coming Soon)",
    slug: null,
    features: ["Electronic Prescriptions", "Integrated with PMS"],
  },
  {
    label: "Online Scheduling",
    slug: null,
    features: ["Patient Self-Scheduling", "Real-Time Availability"],
  },
];

const aiProductItems = [
  { label: "AI Receptionist", slug: "amy" },
  { label: "AI Schedule Optimizer", slug: "leena" },
  { label: "AI Hygiene Recall", slug: "stephanie" },
  { label: "AI Patient Experience", slug: "elise" },
  { label: "AI Case Acceptance", slug: "kim" },
  { label: "AI Patient Intake", slug: "morgan" },
  { label: "AI Practice Manager", slug: "practice-manager" },
  { label: "AI Fax Manager", slug: "fax" },
  { label: "AI Insurance Verification", slug: "amanda" },
  { label: "AI Claim Submission", slug: "ariel" },
  { label: "AI Claim Scrubbing", slug: "ariel-scrubbing" },
  { label: "AI Denial Management", slug: "sarah-denial-analysis" },
  { label: "AI Patient Billing", slug: "kianna" },
  { label: "AI AR & Collections", slug: "alisha" },
  { label: "AI EOB Posting", slug: "alisha-posting" },
  { label: "AI Insurance Calls", slug: "amanda-calling" },
  { label: "AI Copilot", slug: "pms-asha-copilot" },
  { label: "AI Scribe", slug: "pms-ai-scribe" },
  { label: "AI-Voice Perio Charting", slug: "pms-voice-perio" },
  { label: "AI Treatment Planner", slug: "pms-ai-treatment-planner" },
  { label: "AI Revenue Analytics", slug: "pms-revenue-analytics" },
];

const SoftwareRow = ({ item, onNavigate }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${open ? "bg-indigo-50/60" : "bg-slate-50 hover:bg-slate-100/80"}`}
      >
        {open ? (
          <ChevronDown className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        )}
        <span className={`text-[13px] font-semibold flex-1 ${open ? "text-indigo-700" : "text-slate-700"}`}>
          {item.label}
        </span>
        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${open ? "bg-indigo-100 text-indigo-600" : "bg-slate-100 text-slate-400"}`}>
          {item.features.length}
        </span>
      </button>
      {open && (
        <div className="px-3 pb-2 pt-1 bg-indigo-50/30 grid grid-cols-2 gap-x-3 gap-y-0.5">
          {item.features.map((f, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <Check className="w-3 h-3 text-indigo-400 flex-shrink-0" />
              <span className="text-[11px] text-slate-600">{f}</span>
            </div>
          ))}
          {item.slug && (
            <button
              onClick={(e) => { e.stopPropagation(); onNavigate(item.slug); }}
              className="col-span-2 flex items-center gap-1 mt-1 text-[11px] font-semibold text-indigo-500 hover:text-indigo-700 transition-colors"
            >
              View details <ArrowRight className="w-3 h-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const AIOSEmphasisSlide = ({ onNavigate }) => {
  const handleClick = (slug) => {
    if (onNavigate) onNavigate(slug);
  };

  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-6 lg:p-8 relative overflow-hidden font-sans">
      {/* Background blurs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-100/40 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2" />
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-60" />

      <div className="w-full flex flex-col h-full relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-center mb-4 flex-shrink-0"
        >
          <h2 className="text-3xl lg:text-5xl font-extrabold text-slate-900 mb-3 tracking-tight leading-[1.1]">
            10 softwares and 20+ <span className="text-blue-600">AI products</span> in 1 unified platform
          </h2>
        </motion.div>

        {/* Two columns */}
        <div className="flex gap-4 flex-1 min-h-0">
          {/* Left: Softwares — expandable */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col overflow-hidden"
          >
            <div className="flex items-center gap-3 p-4 pb-3 border-b border-slate-100 flex-shrink-0">
              <div className="w-9 h-9 rounded-xl bg-indigo-100 border border-indigo-200 flex items-center justify-center">
                <Monitor className="w-4.5 h-4.5 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">10 Softwares</h3>
                <p className="text-[11px] text-slate-400">Replaces your entire tech stack</p>
              </div>
              <div className="ml-auto px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-100">
                <span className="text-[10px] font-bold text-indigo-600">click to expand</span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
              {softwareCategories.map((item, i) => (
                <SoftwareRow key={i} item={item} onNavigate={handleClick} />
              ))}
            </div>
          </motion.div>

          {/* Right: AI Products — flat list with links */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="flex-1 bg-white rounded-2xl border border-blue-200 shadow-sm flex flex-col overflow-hidden"
          >
            <div className="flex items-center gap-3 p-4 pb-3 border-b border-blue-100 flex-shrink-0">
              <div className="w-9 h-9 rounded-xl bg-blue-100 border border-blue-200 flex items-center justify-center">
                <Bot className="w-4.5 h-4.5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">21 AI Products</h3>
                <p className="text-[11px] text-slate-400">Does the work your staff would</p>
              </div>
              <div className="ml-auto px-2 py-0.5 rounded-full bg-blue-50 border border-blue-100">
                <span className="text-[10px] font-bold text-blue-600">{aiProductItems.length} included</span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <div className="grid grid-cols-2 gap-1.5">
                {aiProductItems.map((item, i) => (
                  <button
                    key={i}
                    onClick={() => handleClick(item.slug)}
                    className="flex items-center gap-2 bg-blue-50/50 rounded-xl border border-blue-100/60 px-3 py-2.5 text-left hover:bg-blue-100/60 hover:border-blue-200 transition-colors group cursor-pointer"
                  >
                    <Check className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                    <span className="text-[13px] font-medium text-slate-700 flex-1">{item.label}</span>
                    <ArrowRight className="w-3 h-3 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AIOSEmphasisSlide;
